import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Container, Form, InputGroup, Nav, Row, Tab } from "react-bootstrap";
import axios from "axios";
import get from "lodash.get";
import { injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { isEmpty } from "lodash";
import { useDebouncedCallback } from "use-debounce";
import useI18n from "../../hooks/useI18n";

// Services
import { userRequestHeaders, userLogin } from '../../services/user';
import { toggleLoading, formatMoney, validate } from "../../services/helpers";
import {jobFetchJob, jobGetJob, jobRemoveCode, jobRemoveJob, jobSetGovidJob} from "../../services/job";

// Components
import InvoiceCard from "../../components/InvoiceCard";
import InvoiceCardSkeleton from "../../components/InvoiceCardSkeleton";
import AlertDialog from "../../components/AlertDialog";
import RetrieveInvoiceModal from "../../components/RetrieveInvoiceModal";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Helmet } from "react-helmet";
import messages from "./Dashboard.messages";
import useUserAccount from "../../hooks/useUserAccount";
import DashboardHeader from "../../components/DashboardHeader";
import useEnv from "../../hooks/useEnv";
import OverpaidWizard from "../../components/OverpaidWizard";
import useSessionStorage from "../../hooks/useSessionStorage";
import useJobs from "../../hooks/useJobs";

const Dashboard = (props) => {
    const { userInitialize, userIsCompany, userIsAuthenticated, userIsLoggedIn } = useUserAccount();
    const userAuthorized = userIsAuthenticated();
    const [showRetrieveModal, setShowRetrieveModal] = useState(false);
    const [retrieveInvoiceError, setRetrieveInvoiceError] = useState({
        message: '',
        type: 'light'
    });
    const [dashboardMessage, setDashboardMessage] = useState({
        message: '',
        type: 'light'
    });
    const [govidJobs, setGovidJobs] = useState(false);
    const [currentTab, setCurrentTab] = useState("invoices");
    const [searchText, setSearchText] = useState("");
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [callback, setCallback] = useState(false);
    const [state, setState] = useState({
        jobid: 0,
        code: "",
        billnum: "",
        reference: "",
        postcode: "",
        jobidForAccountNumber: 0,
        codeForAccountNumber: "",
    });
    const [showOverpaidWizard, setShowOverpaidWizard] = useState(false);
    const [overpaidJob, setOverpaidJob] = useState(null);
    const [overpaidInformation, setOverpaidInformation] = useState({
        show: false,
        account: '',
        amount: '',
        name: '',
        fee: ''
    });
    const searchResultsRef = useRef(null); // Ref for scrolling to search results
    const { getLocale } = useI18n();
    const { isEnvTruthy } = useEnv();
    const { getjobTokenUuid, clearjobTokenUuid } = useSessionStorage();
    const { fetchJobWithUuid } = useJobs();
    const userLocale = getLocale();
    const jobTokenUuid = getjobTokenUuid();
    let searchInUrlParams = false;

    // Govid search with debounced timeout
    const debounced = useDebouncedCallback((value) => {
        // When input has over 3 letters, init search
        if (value.length > 3) {
            setCurrentTab("search");
            getOpenInvoicesByGovid('search', value);
        }

        // When input is empty restore tabs
        if (value.length === 0) {
            setCurrentTab("invoices");
            getOpenInvoicesByGovid('invoices');
        }
    }, props.timeout ?? 1000);

    const allowServing = isEnvTruthy('SHOW_SERVING');

    /**
     * Function for closing the Return payment wizard
     */
    const returnPaymentClose = () => {
        resetRetrieveInvoiceError();
        setShowOverpaidWizard(false);
        setOverpaidJob(null);
    };

    /**
     * Return payment wizard step 1
     * @param formData
     */
    const handleOverpaidJobSearch = (formData) => {
        // Reset errors and other notifications
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();

        // Get the form
        const form = document.getElementById('returnpayment-dpid-form');
        if (form !== undefined && form !== null && validate(form)) {
            // Get the case number and Online ID and form the dpid
            const { caseNumber, onlineId  } = formData;
            if (caseNumber !== '' && onlineId !== '') {
                // Fetch the job data and move to next step
                handleSubmit(`${caseNumber}-${onlineId}`);
            } else {
                setRetrieveInvoiceError({
                    message: lng(messages.unknownError),
                    type: 'danger'
                });
            }
        } else {
            setRetrieveInvoiceError({
                message: lng(messages.unknownError),
                type: 'danger'
            });
        }
    };

    // Translations function
    const lng = props.intl.formatMessage;


    // Get open invoices by user govid
    async function getOpenInvoicesByGovid(eventType = 'invoices', searchTerm = '') {
        setCurrentTab(eventType === 'linksearch' ? 'search' : eventType);
        setGovidJobs(false);

        // Get header information that might contain authorization details
        const headers = userRequestHeaders();

        let _govidJobs = false;
        let route = '/govidsearch';
        let queryParams = '';

        switch (eventType) {
            case "invoices":
                queryParams = '?include_paid=0&exclude_open=0&order=paydate&dir=DESC';
                break;
            case "archive":
                queryParams = '?include_paid=1&exclude_open=1&order=paydate&dir=DESC';
                break;
            case "search":
                queryParams = '?include_paid=1&exclude_open=0&order=paydate&dir=DESC&search=' + searchTerm;
                break;
            case "linksearch":
                route = '/linksearch';
                queryParams = '?include_paid=1&exclude_open=0&order=paydate&dir=DESC&search=' + searchTerm;
                break;
        }

        await axios({
            method: 'GET',
            url: process.env.REACT_APP_API_URL + '/online' + route + queryParams,
            headers: headers
        })
        .then(function (res) {

            // Could be empty body with 204 no content
            if (typeof res.data !== 'undefined' && typeof res.data.govidjob !== 'undefined' && res.data.govidjob !== null) {
                // set jobs
                setGovidJobs(res.data.govidjob);

                _govidJobs = res.data.govidjob;

                // save govid jobs to session storage, too
                jobSetGovidJob(_govidJobs);

                // scroll to the search results only when applicable
                if (searchInUrlParams && searchResultsRef.current) {
                    searchResultsRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }
        })
        .catch(function (error) {
            if (error.response?.status === 401) {
                const alert = {message: lng(messages.authError), type: 'danger'};
                props.history.push({
                    pathname: "/logout",
                    alert
                });
            } else {
                setGovidJobs(error);
                _govidJobs = error;
                return Promise.reject(error);
            }
        })
        .then(function () {
            // Add empty array if no jobs found
            if (_govidJobs === false) {
                setGovidJobs([]);
            }
        });
    }

    useEffect(() => {
        // if uuid exists, first authenticate the user
        if (props.match.params.uuid) {
            // loading animation on
            toggleLoading(true, lng(messages.openingInvoice) + "...");
            userLogin(props.match.params.uuid).then(token => {
                userInitialize(token);
                // get dpid from session storage if user identified with an undirect link
                let dpid = sessionStorage.getItem('dpid');
                // get subscriptionType if user identified with an undirect link to check if user is subscribing to web invoice
                let subscriptionType = sessionStorage.getItem('subscriptionType');

                clearjobTokenUuid();

                // user is subscribing to web invoice when subscriptionType is set to web-invoice
                if (dpid !== null && subscriptionType !== null && subscriptionType === 'web-invoice') {
                    // redirect the user to web invoice subscription page
                    props.history.push("/subscriptions");
                } else if (dpid !== null) {
                    // redirect the user back to page without uuid parameter
                    props.history.push("/jobsearch");

                    dpid = dpid.split('-');
                    setState({ ...state, jobid: dpid[0], code: dpid[1] });

                    setIsAuthenticating(false);
                    // set Callback value to trigger UE
                    setCallback('dpid');

                    getOpenInvoicesByGovid().catch(error => {
                        setGovidJobs(error);
                    });
                } else if (jobTokenUuid) {
                    handleSubmit(jobTokenUuid, true);
                } else {
                    // redirect the user back to page without uuid parameter
                    props.history.push("/jobsearch");

                    // fetch jobs by govid (hetu)
                    getOpenInvoicesByGovid().catch(error => {
                        setGovidJobs(error);
                    });

                    toggleLoading(false);

                    // User has identificated, allow access to / to display the page
                    setIsAuthenticating(false);
                }
            }, (_error) => {
                // token request failed, redirecting back to indentification
                props.history.push("/logout");
                toggleLoading(false);
            });
        } else if (userIsLoggedIn()) {
            // get the company name from the query parameters if available
            const queryParams = new URLSearchParams(props?.location.search);
            const companyName = queryParams.get('s');

            // Allow to display the page
            setIsAuthenticating(false);

            if (companyName !== null) {
                searchInUrlParams = true;
                // search invoices by company name
                getOpenInvoicesByGovid(userAuthorized ? 'search' : 'linksearch', companyName).catch(error => {
                   setGovidJobs(error);
                });
                setSearchText(companyName);
            } else if (userAuthorized) {
                // Fetch the open invoices by govid
                getOpenInvoicesByGovid().catch(error => {
                    setGovidJobs(error);
                });
            }
        }

    }, []);


    /**
     * Trigger when callback variable is set, e.g. when a query needs to be formed jobid and code
     * (page is rendered, or invoice details are opened from the list).
     */
    useEffect(() => {

        if (callback === 'dpid') {
            toggleLoading(false);
            sessionStorage.removeItem('dpid');
            handleSubmit(state.jobid + '-' + state.code);
        }
        else if (callback === 'link') {
            // create query from jobid and code and search it
            const query = state.jobid + '-' + state.code;
            handleSubmit(query);
        }

    }, [callback]);

    /**
     * Check with dpid if requirements for serving are met.
     * Is the job eligible for serving and if serving is signed or not.
     */
    async function checkForServing(govidjob) {
        if (allowServing) {
            const headers = userRequestHeaders();

            return await axios({
                method: 'GET',
                url: process.env.REACT_APP_API_URL + '/online/serving/' + govidjob.dpid,
                headers: headers,
            })
            .then(function (res) {
                // can return 204 NO CONTENT when serving is not eligible (e.g. wrong debt collection chain, country etc.)
                // or 200 OK when eligible for serving, and already signed
                return Promise.resolve(res);
            })
            .catch(function (error) {
                // can return 404 when serving is eligible, but no match found (serving is not signed)
                // or 400 in bad requests
                return Promise.reject(error);
            });
        }
    }

    /**
     * Check if serving needs a signature or not
     *
     * @param {*} govidJob
     * @param {*} response
     * @returns
     */
    function checkServingResponse(govidJob, response) {
		const servingType = response?.data?.data?.serving_type;
		const servedAt = response?.data?.data?.served_at;

		if (servingType === 'letter') {
			govidJob.needsignature = typeof(servedAt) === 'undefined' || String(servedAt).trim() === '';
		}

		return 1;
	}

    /**
     * Link to job using govid search
     *
     * @param event
     * @param dpid
     */
    function linkSubmit(event, dpid) {

        event.preventDefault();

        if (!isEmpty(dpid)) {
            const dpid_array = dpid.split('-');
            setState({
                ...state,
                jobid: dpid_array[0],
                code: dpid_array[1]
            });
            // set value to callback to trigger useEffect
            setCallback('link');
        }
    }
    
    const handleBankAccountSubmitSuccess = (dpid) => {
        handleSubmit(dpid);
    }

    /**
     * Submit the search form, and redirect to job (also used for govid search)
     *
     * @param query
     * @param useUuid
     */
    const handleSubmit = (query, useUuid = false) => {
        // Reset errors and other notifications
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();

        toggleLoading(true, lng(messages.searchingForJob));

        // API call GET /online/jobsearch/{dpid}, jobservice fetches the job details and saves it to storage for later use
        (useUuid ? fetchJobWithUuid(query) : jobFetchJob(query)).then(() => {
            toggleLoading(false);
            // Check the job status code
            const job = jobGetJob();
            // Check if job is overpayment or credit note
            if (job.statuscode === 302 || job.statuscode === 104) {
                // If job status is overpayment or credit note, ensure the Retrieve invoice modal is closed
                setShowRetrieveModal(false);

                // Check if bank account number has already been notified
                if (job.modoverpaid === true) {
                    setOverpaidJob(job);
                    setShowOverpaidWizard(true);
                } else {
                    setOverpaidInformationFromJob(job);
                    returnPaymentClose();
                }
            } else {
                // If job status is not overpayment or credit note, redirect to job's page
                props.history.push('/job');
            }
        }).catch((error) => {
            toggleLoading(false);
            // response exists
            if (error?.response !== undefined) {
                if (error.response.status === 401) {
                    // authentication failed, redirect back to authentication
                    const alert = { message: lng(messages.authError), type: 'danger' };
                    props.history.push({
                        pathname: "/logout",
                        alert
                    });
                } else if (error.response.status === 403) {
                    // Forbidden messages
                    let message = lng(messages.forbiddenError);

                    if (error.response.data?.Message !== undefined) {
                        if (error.response.data.Message === 'Online access log - user access denied') {
                            message = lng(messages.searchLocked);
                        } else if (error.response.data.Message === 'Online jobsearch - company person is not allowed to sign') {
                            message = lng(messages.notAllowedToSign);
                        }
                    }

                    setRetrieveInvoiceError({
                        message: message,
                        type: 'danger'
                    });
                } else {
                    // job not found
                    setRetrieveInvoiceError({
                        message: lng(messages.jobNotFound),
                        type: 'danger'
                    });
                }
            } else {
                // request failed completely
                setRetrieveInvoiceError({
                    message: lng(messages.pleaseTryAgain),
                    type: 'danger'
                });
            }
        });
    }

    /**
     * Get overpaid information for job with notified account number
     * and set it to be displayed to user
     * @param {object} job
     */
    const setOverpaidInformationFromJob = (job) => {
        // Get locale and currency for formatting
        let currency = get(job, 'currency', false);
        currency = currency && currency !== '' ? currency : process.env.REACT_APP_DEFAULT_CURRENCY;

        setOverpaidInformation({
            show: true,
            account: job.overpaid_iban,
            amount: formatMoney(job.modoverpaidamount, userLocale, currency),
            name: job.overpaid_name,
            fee: formatMoney(job.modoverpaidfee, userLocale, currency)
        })
    }

    /**
     * Reset overpaid information
     */
    function resetOverpaidInformation() {
        setOverpaidInformation({
            show: false,
            account: '',
            amount: '',
            name: '',
            fee: ''
        });
    }

    /**
     * Reset general dashboard message
     */
    function resetDashboardMessage() {
        setDashboardMessage({
            message: '',
            type: 'light'
        });
    }

    /**
     * Reset invoice retrieving errors
     */
    function resetRetrieveInvoiceError() {
        setRetrieveInvoiceError({
            message: '',
            type: 'light'
        });
    }

    /**
     * Toggle retrieve invoice modal visibility and clear its errors
     */
    function retrieveModalToggle() {
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();
        setShowRetrieveModal(!showRetrieveModal);
    }

    /**
     * Toggle overpaid wizard visibility and clear its errors
     */
    const overpaidWizardToggle = () => {
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();
        if (showOverpaidWizard) {
            setOverpaidJob(null);
        }
        setShowOverpaidWizard(!showOverpaidWizard);
    }

    /**
     * Reset input field to empty and restore tabs
     */
    function resetSearch() {
        setSearchText("");
        setCurrentTab("invoices");
        getOpenInvoicesByGovid('invoices');
    }

    /**
     * Handle search input events
     * @param event
     */
    function handleSearch(event) {
        setSearchText(event.target.value);
        debounced(event.target.value);
    }

    if (isAuthenticating) {
        return <></>
    }

    return (
        <div className='content flex-fill'>
            <Helmet id='job-helmet'>
                <title>{lng(messages.searchTitle)} | MyRopo</title>
            </Helmet>
            <Container fluid id='dashboard' className='p-0 d-flex h-100'>
                <Container className="p-4 pb-0">
                    <main id="main">
                        {userAuthorized && (
                            <DashboardHeader
                                dashboardMessage={dashboardMessage}
                                overpaidInformation={overpaidInformation}
                                retrieveModalToggle={retrieveModalToggle}
                                overpaidWizardToggle={overpaidWizardToggle}
                            />
                        )}
                        <Tab.Container id="govidsearch" defaultActiveKey="invoices" activeKey={currentTab} onSelect={(key) => setCurrentTab(key)}>
                            <Row className={userAuthorized ? "pt-4" : "pt-md-4"}>
                                <Col xl={userAuthorized ? 6 : 12} md={12} className="order-2 order-xl-1 mt-4 mt-xl-0 online-tabs">
                                    {currentTab === "search" ? (
                                        <Nav
                                            fill
                                            defaultActiveKey="search"
                                            className="online-tabs"
                                            role="tablist"
                                            aria-label={lng(messages.searchResults)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link
                                                    eventKey="search"
                                                >
                                                    {lng(messages.searchResults)}
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    ) : (
                                        <Nav
                                            justify
                                            defaultActiveKey="invoices"
                                            className="online-tabs"
                                            onSelect={(selectedKey) => getOpenInvoicesByGovid(selectedKey)}
                                            role="tablist"
                                            aria-label={lng(messages.tabInvoicesTitle) + " & " + lng(messages.tabArchiveTitle)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link
                                                    eventKey="invoices"
                                                    id="tab-invoices"
                                                    aria-controls="tabpanel-invoices"
                                                    aria-selected={currentTab === "invoices"}
                                                    tabIndex={0}
                                                    role="tab"
                                                >
                                                    {lng(messages.tabInvoicesTitle)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link
                                                    eventKey="archive"
                                                    id="tab-archive"
                                                    aria-controls="tabpanel-archive"
                                                    aria-selected={currentTab === "archive"}
                                                    tabIndex={0}
                                                    role="tab"
                                                >
                                                    {lng(messages.tabArchiveTitle)}</Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    )}
                                </Col>
                                <div className='d-md-none' ref={searchResultsRef} />
                                {/* Search field */}
                                {userAuthorized && (
                                    <Col xl={6} md={12} className="order-1 order-xl-2">
                                        <InputGroup className="has-search">
                                            <InputGroup.Text className="form-control-search" id="search-icon">
                                                <FontAwesomeIcon className="text-body-tertiary" icon={far.faMagnifyingGlass} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder={lng(messages.searchFieldPlaceholder)}
                                                aria-label={lng(messages.searchFieldPlaceholder)}
                                                aria-describedby={lng(messages.searchFieldPlaceholder)}
                                                onChange={handleSearch}
                                                value={searchText}
                                                id="dashboard-search-input"
                                                type="search"
                                                autoComplete="off"
                                                data-testid="dashboard-search-input"
                                            />
                                            {searchText.length > 0 ? (
                                                <Button aria-label="Clear search" variant="outline-secondary" id="clear-search" onClick={() => resetSearch()}>
                                                    <FontAwesomeIcon icon={far.faCircleXmark} />
                                                </Button>
                                            ) : ('')}
                                        </InputGroup>
                                    </Col>
                                )}
                            </Row>
                            <Row>
                                <Col>
                                    <Tab.Content>
                                        <Tab.Pane
                                            id="invoices"
                                            eventKey="invoices"
                                            className="show"
                                            role="tabpanel"
                                            aria-labelledby="tab-invoices"
                                            tabIndex={0}
                                        >
                                            <Row className="g-5 mt-0 bg-light">
                                                <Col>
                                                    {govidJobs !== false && currentTab === "invoices" ? (
                                                        govidJobs.length > 0 ? (
                                                            govidJobs.map((row, i) => {
                                                                // Print open invoices
                                                                return (
                                                                    <button id={"govidjob-" + i} key={row.jobid} className="btn invoice-card" onClick={(e) => linkSubmit(e, row.dpid)}>
                                                                        <InvoiceCard job={row} />
                                                                    </button>
                                                                );
                                                            })
                                                        ) : (govidJobs instanceof Error ? (
                                                            // Error - search failed
                                                            <AlertDialog
                                                                message={lng(messages.govidSearchError)}
                                                                type={'danger'}
                                                            />
                                                        ) : (
                                                            // No open invoices
                                                            <AlertDialog
                                                                message={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noOpen)}
                                                                type={'light'}
                                                            />
                                                        ))
                                                    ) : (
                                                        <InvoiceCardSkeleton count={5} />
                                                    )}
                                                </Col>
                                            </Row>
                                        </Tab.Pane>
                                        <Tab.Pane
                                            id="archive"
                                            eventKey="archive"
                                            role="tabpanel"
                                            aria-labelledby="tab-archive"
                                            tabIndex={0}
                                        >
                                            <Row className="g-5 mt-0 bg-light">
                                                <Col>
                                                    {govidJobs !== false && currentTab === "archive" ? (
                                                        govidJobs.length > 0 ? (
                                                            govidJobs.map((row, i) => {
                                                                // Print paid invoices
                                                                return (
                                                                    <button id={"govidjob-" + i} key={row.jobid} className="btn invoice-card" onClick={(e) => linkSubmit(e, row.dpid)}>
                                                                        <InvoiceCard job={row} />
                                                                    </button>
                                                                );
                                                            })
                                                        ) : (govidJobs instanceof Error ? (
                                                            // Error - search failed
                                                            <AlertDialog
                                                                message={lng(messages.govidSearchError)}
                                                                type={'danger'}
                                                            />
                                                        ) : (
                                                            // No paid invoices
                                                            <AlertDialog
                                                                message={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noArchivedInvoices)}
                                                                type={'light'}
                                                            />
                                                        ))
                                                    ) : (
                                                        <InvoiceCardSkeleton count={5} />
                                                    )}
                                                </Col>
                                            </Row>
                                        </Tab.Pane>
                                        <Tab.Pane
                                            id="search"
                                            eventKey="search"
                                            role="tabpanel"
                                            aria-labelledby="tab-search"
                                            tabIndex={0}
                                        >

                                            <Row className="g-5 mt-0 bg-light">
                                                <Col>
                                                    {govidJobs !== false && currentTab === "search" ? (
                                                        govidJobs.length > 0 ? (
                                                            govidJobs.map((row, i) => {
                                                                // Print paid invoices
                                                                return (
                                                                    <button id={"govidjob-" + i}
                                                                            key={row.jobid}
                                                                            className="btn invoice-card"
                                                                            onClick={(e) => linkSubmit(e, row.dpid)}
                                                                            aria-label={lng(messages.openInvoice)}
                                                                    >
                                                                        <InvoiceCard job={row} />
                                                                    </button>
                                                                );
                                                            })
                                                        ) : (govidJobs instanceof Error ? (
                                                            // Error - search failed
                                                            <AlertDialog
                                                                message={lng(messages.govidSearchError)}
                                                                type={'danger'}
                                                            />
                                                        ) : (
                                                            // No paid invoices
                                                            <AlertDialog
                                                                message={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noSearchResults)}
                                                                type={'light'}
                                                            />
                                                        ))
                                                    ) : (
                                                        <InvoiceCardSkeleton count={5} />
                                                    )}
                                                </Col>
                                            </Row>
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Col>
                            </Row>
                        </Tab.Container>
                        {/* Users with direct link see log in button */}
                        {!userAuthorized && (
                            <Row className="sticky-bottom pb-3">
                                <Col xs={12} xl={{ span: 6, offset: 3 }} className="d-grid ">
                                    <Button variant="primary" onClick={() => props.history.push('/user/logout')}>{lng(messages.logInForAllInvoices)}</Button>
                                </Col>
                            </Row>
                        )}
                    </main>
                </Container>
                <RetrieveInvoiceModal
                    show={showRetrieveModal}
                    hide={retrieveModalToggle}
                    handleSubmit={handleSubmit}
                    error={retrieveInvoiceError}
                    {...props}
                />
                <OverpaidWizard
                    open={showOverpaidWizard}
                    onClose={overpaidWizardToggle}
                    job={overpaidJob}
                    onJobSearchSubmit={handleOverpaidJobSearch}
                    showSearchStep={true}
                    jobSearchError={retrieveInvoiceError}
                    onBankAccountSubmitSuccess={handleBankAccountSubmitSuccess}
                />
            </Container>
        </div>
    );
};

Dashboard.propTypes = {
    'intl': PropTypes.object.isRequired,
    'history': PropTypes.any,
    'match': PropTypes.object,
    'timeout': PropTypes.number,
    'location': PropTypes.object
};

export default injectIntl(Dashboard);
